import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Tooltip,
} from '@mui/material';
import { PopupTransition } from '../../../../components/@extended/Transitions';
import MainCard from '../../../../components/MainCard';
import { Box, Stack } from '@mui/system';
import { FormattedMessage } from 'react-intl';
import BaseTable from '../../../../components/base-table';
import { useConfigManagement, useConfigTableColumnsConfig } from './hooks';
import { GenericTableConfigViewEntry } from './dto';
import { DefaultVisibleConfigsTableColumnConfig } from './consts';
import { useCallback, useState } from 'react';
import { NewConfigDialog } from './partials/new-config-dialog';
import { createConfig } from '../../../api/generic-tables/create-config';
import { NewConfigDialogFormValues } from './partials/new-config-dialog/dto';
import { GenericTableConfigDto } from '../../../api/generic-tables/dto';
import { UpdateConfigDialog } from './partials/update-config-dialog';
import { Column } from 'react-table';

export const GenericTablesConfigPage = () => {
    const [newConfigDialogOpened, setNewConfigDialogOpened] = useState(false);
    const {
        configs,
        fetchConfigs,
        isManagementAllowed,
        pageCount,
        updateConfig,
        duplicateConfig,
        deleteConfig,
        refreshConfigs,
    } = useConfigManagement();
    const [editedConfig, setEditedConfig] = useState<GenericTableConfigDto | null>(null);
    const [selectedConfig, setSelectedConfig] = useState<GenericTableConfigDto | null>(null);
    const [configToDelete, setConfigToDelete] = useState<GenericTableConfigDto | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const onNewConfigSubmit = useCallback(
        async (data: NewConfigDialogFormValues) => {
            if (!data.name || !data.dataSource?.fileId || !data.dataSource?.sheetId) {
                return;
            }
            const newlyCreatedConfig = await createConfig({
                name: data.name,
                dataSource: data.dataSource,
            });
            await refreshConfigs();
            setNewConfigDialogOpened(false);
            setEditedConfig(newlyCreatedConfig);
        },
        [refreshConfigs],
    );

    const handleDuplicate = useCallback(async (config: GenericTableConfigDto) => {
        setSelectedConfig(config);
        setConfirmDialogOpen(true);
    }, []);

    const handleDelete = useCallback(async (config: GenericTableConfigDto) => {
        setConfigToDelete(config);
        setConfirmDeleteDialogOpen(true);
    }, []);

    const confirmDuplicate = useCallback(async () => {
        if (selectedConfig) {
            const duplicatedConfig = await duplicateConfig(selectedConfig.id);
            if (duplicatedConfig) {
                await refreshConfigs();
                setSnackbarMessage(`Table configuration ${duplicatedConfig.name} has been created`);
                setSnackbarOpen(true);
            } else {
                setSnackbarMessage('Failed to duplicate configuration. Please try again.');
                setSnackbarOpen(true);
            }
            setConfirmDialogOpen(false);
            setSelectedConfig(null);
        }
    }, [selectedConfig, duplicateConfig, refreshConfigs]);

    const confirmDelete = useCallback(async () => {
        if (!configToDelete) return;
        try {
            const deletedConfig = await deleteConfig(configToDelete.id);
            setSnackbarMessage(
                deletedConfig.status === 200
                    ? `Table configuration ${configToDelete.name} has been deleted`
                    : `Failed to delete configuration ${configToDelete.name}. Please try again.`,
            );
            setSnackbarOpen(true);
            setConfirmDeleteDialogOpen(false);
        } catch (error) {
            setSnackbarMessage('Failed to delete file. Please try again.');
        } finally {
            setSnackbarOpen(true);
            setConfirmDeleteDialogOpen(false);
            setSelectedConfig(null);
        }
        setConfirmDeleteDialogOpen(false);
        setSelectedConfig(null);
    }, [configToDelete, deleteConfig]);

    const columnsConfig = useConfigTableColumnsConfig({
        onEdit: setEditedConfig,
        onDuplicate: handleDuplicate,
        onDelete: handleDelete,
    });

    return (
        <>
            <Dialog
                maxWidth="sm"
                TransitionComponent={PopupTransition}
                fullWidth
                open={newConfigDialogOpened}
                sx={{ '& .MuiDialog-paper': { p: 0 } }}
            >
                <NewConfigDialog onSubmit={onNewConfigSubmit} onCancel={() => setNewConfigDialogOpened(false)} />
            </Dialog>
            <Dialog
                maxWidth="lg"
                TransitionComponent={PopupTransition}
                fullWidth
                open={Boolean(editedConfig)}
                sx={{ '& .MuiDialog-paper': { p: 0 } }}
            >
                {editedConfig && (
                    <UpdateConfigDialog
                        config={editedConfig}
                        onSubmit={async (data) => {
                            await updateConfig(editedConfig.id, { dataSource: editedConfig.dataSource, ...data });

                            setEditedConfig(null);
                        }}
                        onCancel={() => {
                            setEditedConfig(null);
                        }}
                    />
                )}
            </Dialog>
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Duplicate table configuration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {selectedConfig && (
                            <p>
                                Do you want to duplicate this table configuration <strong>{selectedConfig.name}</strong>
                                ?
                                <br />A new table configuration <strong>Clone_{selectedConfig.name}</strong> will be
                                created.
                            </p>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDialogOpen(false)}
                        sx={{
                            color: 'rgb(255,100,100)',
                            border: 'none',
                            '&:hover': {
                                color: 'rgb(168, 7, 26)',
                                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                                border: 'none',
                            },
                            '&:focus': {
                                color: 'rgb(168, 7, 26)',
                                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                                outline: 'none',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={confirmDuplicate} color="primary" variant="contained">
                        Duplicate
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)}>
                <DialogTitle>Delete table configuration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {configToDelete && (
                            <p>
                                Do you want to delete this table configuration{' '}
                                <strong>
                                    {configToDelete.id}. {configToDelete.name}
                                </strong>
                                ?
                            </p>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDeleteDialogOpen(false)}
                        sx={{
                            color: 'rgb(255,100,100)',
                            border: 'none',
                            '&:hover': {
                                color: 'rgb(168, 7, 26)',
                                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                                border: 'none',
                            },
                            '&:focus': {
                                color: 'rgb(168, 7, 26)',
                                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                                outline: 'none',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="primary" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <MainCard content={false}>
                <Stack direction="column" component="div">
                    <Box sx={{ p: 2 }}>
                        <Tooltip
                            title={
                                !isManagementAllowed ? <FormattedMessage id="inside-fifa.no-permission-tip" /> : null
                            }
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setNewConfigDialogOpened(true);
                                    }}
                                    disabled={!isManagementAllowed}
                                >
                                    <FormattedMessage id="inside-fifa.generic-tables.create-new-table" />
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Stack>
                <BaseTable<GenericTableConfigViewEntry>
                    data={configs}
                    fetchData={fetchConfigs}
                    pageCount={pageCount}
                    defaultColumnConfig={columnsConfig as Column[]}
                    defaultVisibleColumnConfig={DefaultVisibleConfigsTableColumnConfig}
                />
            </MainCard>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </>
    );
};
